<template>
  <div class="calendar" :class="`calendar--${size}`">
    <CalendarHeader
      :size="size"
      :monthName="headerDisplay"
      :selectedYear="selectedYear"
      @navigate-month="navigateYear"
      @toggle-year-selector="toggleYearSelector"
    />

    <div class="calendar-wrapper">
      <transition name="fade" mode="out-in">
        <MonthsGrid
          v-if="!showingYears"
          :class="clsx('months-grid', { 'months-grid__alignment': gridFixedHeight })"
          :show="!showingYears"
          :size="size"
          :monthNames="monthNames"
          :currentDate="currentDate"
          :selectedYear="selectedYear"
          :mode="'month-selection'"
          :range="range"
          :startDate="startDate"
          :endDate="endDate"
          :maxRangeLimit="maxRangeMonths"
          :limitUnit="'month'"
          :selectingRange="selectingRange"
          @select-month="handleMonthSelection"
          key="months-grid"
        />
        <YearsGrid
          v-else
          :class="clsx('years-grid', { 'years-grid__alignment': gridFixedHeight })"
          :show="showingYears"
          :size="size"
          :yearOptions="yearOptions"
          :selectedYear="selectedYear"
          :range="range"
          :startDate="startDate"
          :endDate="endDate"
          :maxRangeLimit="maxRangeMonths"
          :limitUnit="'month'"
          :selectingRange="selectingRange"
          @select-year="handleYearSelection"
          key="years-grid"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';
import { moment } from '../../lib';
import CalendarHeader from '../calendar-header';
import MonthsGrid from '../months-grid';
import YearsGrid from '../years-grid';

export default {
  mixins: [moment],
  components: { CalendarHeader, MonthsGrid, YearsGrid },
  props: {
    /**
     * The currently selected month(s). Can be a Date object for single selection,
     * or an array of two Date objects for a range.
     * @type {Date | Date[] | null}
     */
    value: {
      type: [Date, Array],
      default: null,
    },
    /**
     * If true, allows selection of a range of months.
     * @type {boolean}
     */
    range: {
      type: Boolean,
      default: false,
    },
    /**
     * The maximum number of months allowed in a selected range (inclusive).
     * If null, there is no limit.
     * @type {number | null}
     */
    maxRangeMonths: {
      type: Number,
      default: null,
      validator: (value) => value === null || value > 0,
    },
    /**
     * The starting year for the year selection dropdown.
     * If null, calculated dynamically around the current year.
     * @type {number | null}
     */
    startYear: {
      type: Number,
      default: null,
    },
    /**
     * The ending year for the year selection dropdown.
     * If null, calculated dynamically around the current year.
     * @type {number | null}
     */
    endYear: {
      type: Number,
      default: null,
    },
    /**
     * Defines the size of the entire calendar component.
     * @type {string}
     * @values 'normal', 'small'
     */
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value),
    },
    /**
     * If true, all calendar grids will have the same height.
     */
    gridFixedHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentDate: this.getMoment(),
      selectedMonth: null,
      startDate: null,
      endDate: null,
      selectingRange: false,
      showingYears: false,
    };
  },
  computed: {
    /**
     * Generates an array of localized month names (e.g., 'Січень', 'Лютий').
     * @returns {Array<string>}
     */
    monthNames() {
      const locale = 'uk-UA';
      const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
      const months = [];
      for (let i = 0; i < 12; i++) {
        const date = new Date(2000, i, 1); // Use a fixed year like 2000 to get month names
        months.push(formatter.format(date));
      }
      return months;
    },
    /**
     * The currently selected year, derived from currentDate.
     * @returns {number}
     */
    selectedYear: {
      get() {
        return this.getYearMoment(this.currentDate);
      },
      set(year) {
        this.currentDate = this.setYearMoment(this.currentDate, year);
      },
    },
    /**
     * Generates an array of years for the year selection grid based on startYear/endYear props
     * or a dynamic range around the current year.
     * @returns {Array<number>}
     */
    yearOptions() {
      const years = [];
      let minYear;
      let maxYear;

      if (this.startYear !== null && this.endYear !== null) {
        minYear = this.startYear;
        maxYear = this.endYear;
      } else {
        const baseYear = this.getYearMoment(this.currentDate);
        minYear = baseYear - 20;
        maxYear = baseYear + 20;
      }

      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }
      return years;
    },
    /**
     * Computes the display string for the calendar header based on selection mode.
     * @returns {string} Formatted month(s) and year(s).
     */
    headerDisplay() {
      const currentCalendarSelectedYear = this.selectedYear;

      if (this.range) {
        if (this.startDate && this.endDate) {
          const startYear = this.getYearMoment(this.startDate);
          const endYear = this.getYearMoment(this.endDate);
          const startMonthName = this.formatMoment(this.startDate, 'MMMM');
          const endMonthName = this.formatMoment(this.endDate, 'MMMM');

          let startDisplay = startMonthName;
          if (startYear !== currentCalendarSelectedYear) {
            startDisplay += ` ${startYear}`;
          }

          let endDisplay = endMonthName;
          if (endYear !== currentCalendarSelectedYear) {
            endDisplay += ` ${endYear}`;
          }

          if (startYear === endYear) {
            return `${startMonthName} - ${endMonthName}${startYear !== currentCalendarSelectedYear ? ` ${startYear}` : ''}`;
          } else {
            return `${startDisplay} - ${endDisplay}`;
          }
        } else if (this.startDate && this.selectingRange) {
          const startYear = this.getYearMoment(this.startDate);
          const startMonthName = this.formatMoment(this.startDate, 'MMMM');
          return `${startMonthName}${startYear !== currentCalendarSelectedYear ? ` ${startYear}` : ''} -`;
        } else {
          const displayMonthName = this.formatMoment(this.currentDate, 'MMMM');
          return `${displayMonthName}`;
        }
      } else {
        const displayDate = this.selectedMonth || this.currentDate;
        const displayYear = this.getYearMoment(displayDate);
        const displayMonthName = this.formatMoment(displayDate, 'MMMM');
        return `${displayMonthName}${displayYear !== currentCalendarSelectedYear ? ` ${displayYear}` : ''}`;
      }
    },
  },
  methods: {
    clsx,
    /**
     * Navigates the calendar to the previous or next year.
     * @param {number} direction - -1 for previous year, 1 for next year.
     */
    navigateYear(direction) {
      this.currentDate = this.addMoment(this.currentDate, direction, 'year');
      this.hideAllSelectors();
    },
    /**
     * Toggles the visibility of the year selector grid.
     */
    toggleYearSelector() {
      this.showingYears = !this.showingYears;
    },
    /**
     * Calculates the number of months between two Moment.js objects (inclusive).
     * @param {object} startMoment - The start Moment.js object.
     * @param {object} endMoment - The end Moment.js object.
     * @returns {number} The number of months in the range (e.g., Jan-Jan is 1 month).
     */
    getMonthsDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);

      const yearsDiff = d2.getFullYear() - d1.getFullYear();
      const monthsDiff = d2.getMonth() - d1.getMonth();

      return (yearsDiff * 12) + monthsDiff + 1; // +1 for inclusive range
    },
    /**
     * Handles the selection of a month from the MonthsGrid.
     * Manages single month selection or range selection logic.
     * Emits 'input', 'month-selected', or 'range-selected' events.
     * @param {number} monthIndex - The 0-indexed month (0 for January).
     */
    handleMonthSelection(monthIndex) {
      const selectedMonthMoment = this.setMonthMoment(this.cloneMoment(this.currentDate), monthIndex);

      if (this.range) {
        if (!this.selectingRange || this.endDate) {
          this.startDate = this.startOfMoment(selectedMonthMoment, 'month');
          this.endDate = null;
          this.selectingRange = true;
          this.$emit('range-start-selected', this.toDateMoment(this.startDate));
        } else {
          let tempStartDate = this.startDate;
          let tempEndDate = this.endOfMoment(selectedMonthMoment, 'month');

          if (this.isAfterMoment(tempStartDate, tempEndDate, 'month')) {
            [tempStartDate, tempEndDate] = [tempEndDate, tempStartDate];
            tempStartDate = this.startOfMoment(tempStartDate, 'month');
            tempEndDate = this.endOfMoment(tempEndDate, 'month');
          } else {
            tempStartDate = this.startOfMoment(tempStartDate, 'month');
          }

          if (this.maxRangeMonths !== null) {
            const monthsInSelection = this.getMonthsDifference(tempStartDate, tempEndDate);
            if (monthsInSelection > this.maxRangeMonths) {
              this.$emit('range-limit-exceeded', {
                selectedMonths: monthsInSelection,
                maxAllowed: this.maxRangeMonths,
              });
              return;
            }
          }

          this.startDate = tempStartDate;
          this.endDate = tempEndDate;
          this.selectingRange = false;

          this.$emit('range-selected', {
            startDate: this.toDateMoment(this.startDate),
            endDate: this.toDateMoment(this.endDate),
          });
          this.$emit('input', [
            this.toDateMoment(this.startDate),
            this.toDateMoment(this.endDate),
          ]);
        }
      } else {
        this.selectedMonth = selectedMonthMoment;
        const resultStartDate = this.startOfMoment(this.selectedMonth, 'month');
        const resultEndDate = this.endOfMoment(this.selectedMonth, 'month');

        this.$emit('input', [
          this.toDateMoment(resultStartDate),
          this.toDateMoment(resultEndDate),
        ]);
        this.$emit('month-selected', {
          startDate: this.toDateMoment(resultStartDate),
          endDate: this.toDateMoment(resultEndDate),
        });
      }
      this.hideAllSelectors();
    },
    /**
     * Handles the selection of a year from the YearsGrid.
     * Updates the current year and hides the year selector.
     * @param {number} year - The selected year.
     */
    handleYearSelection(year) {
      this.currentDate = this.setYearMoment(this.currentDate, year);
      this.hideAllSelectors();
    },
    /**
     * Hides the year selector grid.
     */
    hideAllSelectors() {
      this.showingYears = false;
    },
  },
  watch: {
    /**
     * Watches the 'value' prop to update internal state.
     * This handles initial value setting and external changes.
     */
    value: {
      immediate: true,
      handler(newValue) {
        if (this.range) {
          if (Array.isArray(newValue) && newValue.length === 2 && newValue[0] && newValue[1]) {
            this.startDate = this.getMoment(newValue[0]);
            this.endDate = this.getMoment(newValue[1]);
            this.currentDate = this.cloneMoment(this.startDate);
            this.selectingRange = false;
          } else if (newValue && !Array.isArray(newValue)) {
            this.startDate = this.getMoment(newValue);
            this.endDate = null;
            this.currentDate = this.cloneMoment(this.startDate);
            this.selectingRange = true;
          } else {
            this.startDate = null;
            this.endDate = null;
            this.selectingRange = false;
            this.currentDate = this.getMoment();
          }
        } else {
          if (newValue) {
            this.selectedMonth = this.getMoment(newValue);
            this.currentDate = this.cloneMoment(this.selectedMonth);
          } else {
            this.selectedMonth = null;
            this.currentDate = this.getMoment();
          }
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$base-font-size: 20px;

.calendar {
  background: white;
  font-family: inherit;
  font-size: $base-font-size;
  font-weight: 400;
  color: #09090b;
  overflow: hidden;

  &-wrapper {
    position: relative;
    flex-grow: 1;
    max-height: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &--small {
    font-size: $base-font-size * $small-scale-factor;
    width: 273px;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.15s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
}

.months-grid {
  &__alignment {
    padding: 56px 0;
  }
}

.years-grid {
  &__alignment {
    padding: 0;
  }
}
</style>
